import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) || p.tag.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [query, products]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const goTo = (id) => {
    navigate(`/product/${id}`);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="search-overlay"
          className="fixed inset-0 z-[90] overflow-y-auto bg-bone"
        >
          <div className="mx-auto max-w-4xl px-6 pb-20 pt-24 md:pt-32">
            <div className="flex items-center justify-between gap-6 border-b-2 border-ink pb-4">
              <input
                autoFocus
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the goods…"
                className="w-full bg-transparent font-serif text-3xl font-medium tracking-tight text-ink placeholder:text-smoke/50 focus:outline-none md:text-5xl"
              />
              <button
                data-testid="search-close"
                onClick={onClose}
                aria-label="Close search"
                className="shrink-0 text-smoke transition-colors duration-300 hover:text-ink"
              >
                <X className="h-7 w-7" strokeWidth={1.5} />
              </button>
            </div>

            {query.trim() && (
              <p className="mt-6 text-xs uppercase tracking-[0.25em] text-smoke">
                {results.length
                  ? `${results.length} result${results.length > 1 ? "s" : ""}`
                  : "Nothing found — try another word"}
              </p>
            )}

            <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-4">
              {results.map((p) => (
                <button
                  key={p.id}
                  data-testid={`search-result-${p.id}`}
                  onClick={() => goTo(p.id)}
                  className="group text-left"
                >
                  <div className="aspect-square overflow-hidden bg-white">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-3 line-clamp-1 font-serif text-lg font-medium tracking-tight text-ink">
                    {p.name}
                  </p>
                  <p className="mt-0.5 text-sm text-smoke">{fmt(p.price)}</p>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
